import React from "react";
import { BrowserRouter, HashRouter, Routes, Route, Navigate, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { createGuestOrder, reconcileGuestCheckout, getGuestOrder, cancelGuestOrder, joinGuestWaitlist, getWaitlistCount, submitContact, getPublicFlags, getGuestFacilityDetail, captureAttribution, captureGrowthEvent, getGrowthSessionId, setGrowthSessionId } from "./api";
import { useTheme } from "./theme";
import { I } from "./icons";
import { FACILITIES, SAMPLE } from "./data";
// Step paths + sender shape live in flow_steps.js (dependency-free) so the
// router knows them without pulling the stage UI into the initial download.
import { FLOW_STEPS, LEGACY_STEP_TO_STAGE, PATH_TO_STEP, EMPTY_SENDER, firstIncompleteStep } from "./flow_steps";
import { SupportWidget } from "./support_widget";
import { FAQ_SECTIONS, HELP_TOPIC_REDIRECTS } from "./faq_data";
import { LuxuryHome, SiteHeader, SiteFooter } from "./home";
import { SiteFooterContext } from "./site_footer_context";
import { BrowardPostcardGuide, FacilityTrustCard, IndependentServiceDisclosure, TrustConfidence, recordDay25ExperimentConversion, useDay25Locale } from "./day25";
import { isLookupOrderId } from "./order_id";
import { useFlowLocale, useSiteLocale } from "./flow_copy";

// Split out of the initial bundle.
//
// The operator console and the 13-surface design workbench are reachable only
// at /admin and /workbench. They were statically imported, so every customer
// downloaded both to render the landing page — and this audience is
// overwhelmingly on phones, frequently on metered or prepaid data. Neither
// route is prerendered, so nothing in scripts/prerender.js depends on them
// being present synchronously.
// Each of these is reachable from exactly one route. /status, /terms and
// /privacy are prerendered, and scripts/prerender.js waits for networkidle, so
// the chunk resolves before the snapshot is taken and the static HTML is
// unchanged.
const PublicStatusPage = React.lazy(() =>
  import("./ops_additions").then((m) => ({ default: m.PublicStatusPage })));
// Harvest integration (2026-10-21): the seasonal send-by page is its own
// chunk, reached from the homepage's date-gated seasonal banner.
const ChristmasPage = React.lazy(() =>
  import("./seasonal").then((m) => ({ default: m.ChristmasPage })));
// V37 giveaway: the public free-card campaign page, its own chunk like the
// seasonal page — homepage visitors should not pay for its form code.
const GiveawayPage = React.lazy(() =>
  import("./giveaway").then((m) => ({ default: m.GiveawayPage })));
const AdminLogin = React.lazy(() =>
  import("./admin_extras").then((m) => ({ default: m.AdminLogin })));
const WaitlistPage = React.lazy(() =>
  import("./v19_additions").then((m) => ({ default: m.WaitlistPage })));

const AdminDashboard = React.lazy(() =>
  import("./admin").then((m) => ({ default: m.AdminDashboard })));

// WEB-08 §15.8 admin route separation — every required surface gets its own
// route, backed by staged endpoints where they exist.
const AdminOrdersRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminOrdersRoute })));
const AdminGiveawayRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminGiveawayRoute })));
const AdminOrderDetailRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminOrderDetailRoute })));
const AdminOrdersReviewRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminOrdersReviewRoute })));
const AdminOrdersFailedRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminOrdersFailedRoute })));
const AdminOrdersCancellationsRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminOrdersCancellationsRoute })));
const AdminOrdersRefundsRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminOrdersRefundsRoute })));
const AdminComplianceRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminComplianceRoute })));
const AdminFacilitiesRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminFacilitiesRoute })));
const AdminFacilityRulesRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminFacilityRulesRoute })));
const AdminCustomersRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminCustomersRoute })));
const AdminPaymentsRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminPaymentsRoute })));
const AdminProvidersRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminProvidersRoute })));
const AdminProviderDetailRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminProviderDetailRoute })));
const AdminWebhooksRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminWebhooksRoute })));
const AdminJobsRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminJobsRoute })));
const AdminSystemHealthRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminSystemHealthRoute })));
const AdminAlertsRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminAlertsRoute })));
const AdminAuditLogRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminAuditLogRoute })));
const AdminUsersRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminUsersRoute })));
const AdminRolesRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminRolesRoute })));
const AdminSecurityRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminSecurityRoute })));
const AdminFeatureFlagsRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminFeatureFlagsRoute })));
const AdminMaintenanceRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminMaintenanceRoute })));
const AdminEmergencyControlsRoute = React.lazy(() =>
  import("./admin_routes").then((m) => ({ default: m.AdminEmergencyControlsRoute })));

// Customer account surface (A1): login-free guest checkout is untouched; these
// pages are the account hub, lazy-split like everything else.
const LoginPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.LoginPage })));
const SignupPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.SignupPage })));
const VerifyEmailPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.VerifyEmailPage })));
const ForgotPasswordPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.ResetPasswordPage })));
const AccountDashboard = React.lazy(() =>
  import("./account").then((m) => ({ default: m.AccountDashboard })));
const AccountOrdersPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.AccountOrdersPage })));
const ClaimOrderPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.ClaimOrderPage })));
const AccountNotificationsPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.AccountNotificationsPage })));
const AccountPreferencesPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.AccountPreferencesPage })));
const AccountSecurityPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.AccountSecurityPage })));
const AccountPrivacyPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.AccountPrivacyPage })));
const AccountDeletionConfirmPage = React.lazy(() =>
  import("./account").then((m) => ({ default: m.AccountDeletionConfirmPage })));
// V44: the claim email used to link to /claim-order, which never existed.
// Keep those already-sent links working by forwarding the token.
function ClaimOrderRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/account/claim-order${search || ""}`} replace />;
}
const AccountRemindersPage = React.lazy(() =>
  import("./account_reminders").then((m) => ({ default: m.AccountRemindersPage })));

// Supporting routes, split out of the initial download. Somebody arriving to
// send a card does not need the pricing page's markup to render the homepage,
// and this audience is overwhelmingly on phones and metered data.
const HowItWorksPage = React.lazy(() =>
  import("./pages_lux").then((m) => ({ default: m.HowItWorksPage })));
const PricingPage = React.lazy(() =>
  import("./pages_lux").then((m) => ({ default: m.PricingPage })));
const FacilityRulesPage = React.lazy(() =>
  import("./pages_lux").then((m) => ({ default: m.FacilityRulesPage })));
const FacilitiesIndexPage = React.lazy(() =>
  import("./pages_lux").then((m) => ({ default: m.FacilitiesIndexPage })));
const HelpPage = React.lazy(() =>
  import("./pages_lux").then((m) => ({ default: m.HelpPage })));
const OrderHelpPage = React.lazy(() =>
  import("./pages_lux").then((m) => ({ default: m.OrderHelpPage })));
const PolicyStatusPage = React.lazy(() =>
  import("./pages_lux").then((m) => ({ default: m.PolicyStatusPage })));
const AccessibilityPage = React.lazy(() =>
  import("./pages_lux").then((m) => ({ default: m.AccessibilityPage })));
const PrivateOrderPage = React.lazy(() =>
  import("./private_order").then((m) => ({ default: m.PrivateOrderPage })));

// The policy surfaces WEB-05 requires before live payment. Split out: nobody
// reaching the homepage needs the refund policy's markup to render it.
const RefundPolicyPage = React.lazy(() =>
  import("./policies").then((m) => ({ default: m.RefundPolicyPage })));
const CancellationPolicyPage = React.lazy(() =>
  import("./policies").then((m) => ({ default: m.CancellationPolicyPage })));
const AcceptableUsePage = React.lazy(() =>
  import("./policies").then((m) => ({ default: m.AcceptableUsePage })));
const ContentPolicyPage = React.lazy(() =>
  import("./policies").then((m) => ({ default: m.ContentPolicyPage })));
const DataRetentionPage = React.lazy(() =>
  import("./policies").then((m) => ({ default: m.DataRetentionPage })));
const DisclosuresPage = React.lazy(() =>
  import("./policies").then((m) => ({ default: m.DisclosuresPage })));
const Workbench = React.lazy(() =>
  import("./app").then((m) => ({ default: m.App })));

// The send-flow steps, split out of the initial download. Somebody landing
// on the homepage does not need the photo cropper, the recipient form or the
// checkout screen to see it — and this audience is overwhelmingly on phones,
// frequently on metered or prepaid data. The step paths and sender shape stay
// static (flow_steps.js, dependency-free) so the router still resolves deep
// links and validates the draft without the UI.
const StepFacility = React.lazy(() =>
  import("./flow_stages").then((m) => ({ default: m.StepFacility })));
const StepRecipient = React.lazy(() =>
  import("./flow_stages").then((m) => ({ default: m.StepRecipient })));
const StepPhoto = React.lazy(() =>
  import("./flow_stages").then((m) => ({ default: m.StepPhoto })));
const StepMessage = React.lazy(() =>
  import("./flow_stages").then((m) => ({ default: m.StepMessage })));
const StepRules = React.lazy(() =>
  import("./flow_stages").then((m) => ({ default: m.StepRules })));
const StepReview = React.lazy(() =>
  import("./flow_stages").then((m) => ({ default: m.StepReview })));
// Legacy stage wrappers — kept for the design workbench (app.jsx).
const StageCreate = React.lazy(() =>
  import("./flow_stages").then((m) => ({ default: m.StageCreate })));
const StageRecipient = React.lazy(() =>
  import("./flow_stages").then((m) => ({ default: m.StageRecipient })));
const StageReview = React.lazy(() =>
  import("./flow_stages").then((m) => ({ default: m.StageReview })));
const ScreenPay = React.lazy(() =>
  import("./screens").then((m) => ({ default: m.ScreenPay })));

// Info pages: only their routes need them.
const AboutPage = React.lazy(() =>
  import("./info_pages").then((m) => ({ default: m.AboutPage })));
const FAQPage = React.lazy(() =>
  import("./info_pages").then((m) => ({ default: m.FAQPage })));
const ContactPage = React.lazy(() =>
  import("./info_pages").then((m) => ({ default: m.ContactPage })));
const SecurityPage = React.lazy(() =>
  import("./info_pages").then((m) => ({ default: m.SecurityPage })));

// Guest extras: legal/cancel/deletion pages are their own routes.
const LegalPage = React.lazy(() =>
  import("./guest_extras").then((m) => ({ default: m.LegalPage })));
const CancelOrder = React.lazy(() =>
  import("./guest_extras").then((m) => ({ default: m.CancelOrder })));
const PrivacyDeletion = React.lazy(() =>
  import("./guest_extras").then((m) => ({ default: m.PrivacyDeletion })));
const OrderLookup = React.lazy(() =>
  import("./guest_extras").then((m) => ({ default: m.OrderLookup })));
const ScheduledSendPicker = React.lazy(() =>
  import("./guest_extras").then((m) => ({ default: m.ScheduledSendPicker })));

// Post-purchase, tracking and error pages: only their routes need them.
const ScreenSuccess = React.lazy(() =>
  import("./success_and_errors").then((m) => ({ default: m.ScreenSuccess })));
const PublicTrackingCard = React.lazy(() =>
  import("./success_and_errors").then((m) => ({ default: m.PublicTrackingCard })));
const ScreenPaymentFailed = React.lazy(() =>
  import("./success_and_errors").then((m) => ({ default: m.ScreenPaymentFailed })));
const DeletionConfirmPage = React.lazy(() =>
  import("./final_additions").then((m) => ({ default: m.DeletionConfirmPage })));
const OrderNotFound = React.lazy(() =>
  import("./final_additions").then((m) => ({ default: m.OrderNotFound })));

// Port additions: user dashboard, admin ops boards, mail guide, services.
// Lazy so each route only loads its own chunk.
const Dashboard = React.lazy(() =>
  import("./dashboard").then((m) => ({ default: m.default })));
const PastOrders = React.lazy(() =>
  import("./past_orders").then((m) => ({ default: m.default })));
const ReferralDashboard = React.lazy(() =>
  import("./referral_dashboard").then((m) => ({ default: m.default })));
const Wallet = React.lazy(() =>
  import("./wallet").then((m) => ({ default: m.default })));
const ScheduledSends = React.lazy(() =>
  import("./scheduled_sends").then((m) => ({ default: m.default })));
const NotifyPrefs = React.lazy(() =>
  import("./notify_prefs").then((m) => ({ default: m.default })));
const AddressBook = React.lazy(() =>
  import("./address_book").then((m) => ({ default: m.default })));
const MailGuidePage = React.lazy(() =>
  import("./info_pages").then((m) => ({ default: m.MailGuidePage })));
const ServicesPage = React.lazy(() =>
  import("./info_pages").then((m) => ({ default: m.ServicesPage })));
const AdminPipelineView = React.lazy(() =>
  import("./admin_pipeline").then((m) => ({ default: m.AdminPipelineView })));
const AdminRejectionsView = React.lazy(() =>
  import("./admin_rejections").then((m) => ({ default: m.AdminRejectionsView })));
const AdminLiabilityView = React.lazy(() =>
  import("./admin_liability").then((m) => ({ default: m.AdminLiabilityView })));
const AdminSupportView = React.lazy(() =>
  import("./admin_support").then((m) => ({ default: m.AdminSupportView })));

// Global chrome controls (language, theme, accent, a11y) behind one slim
// toggle on every Shell page — see the Shell component below. Collapsed by
// default (owner direction, 2026-10-20: the open strip ate ~105px of the
// first screen on a phone before any content). Every control stays one tap
// away in the expanded panel, and the header keeps its own Preferences
// sheet with the same controls.
const ChromeControls = React.lazy(() =>
  import("./chrome").then((m) => ({
    default: function ChromeControlStrip() {
      const [open, setOpen] = React.useState(false);
      const { t } = useSiteLocale();
      return (
        <div className="cc-utility-strip" role="region" aria-label="Display and language settings">
          <m.RouteAnnouncer />
          <button
            type="button"
            className="cc-utility-toggle"
            aria-expanded={open}
            aria-controls="cc-utility-panel"
            onClick={() => setOpen((v) => !v)}
          >
            <span aria-hidden="true">⚙</span> {t.prefsLabel}
          </button>
          {open && (
            <div id="cc-utility-panel" className="cc-utility-panel">
              <m.SiteLanguageSwitcher compact />
              <m.ThemeSwitch />
              <m.SiteAccentSwitcher />
              <m.A11yControls />
            </div>
          )}
        </div>
      );
    },
  })));

// Deliberately plain. A spinner that flashes for 200ms on a fast connection is
// noise; on a slow one, text that says what is happening beats an animation.
function ChunkLoading() {
  return (
    <div role="status" aria-live="polite" style={{
      minHeight: "60vh", display: "grid", placeItems: "center",
      color: "var(--ink-300)", fontSize: 14,
    }}>Loading…</div>
  );
}

// Real product routes for the screens the design handoff specifies
// (carecard.express/, /send, /<publicId> tracking, info pages, /status,
// admin.carecard.express → /admin). The 13-surface design-review workbench
// stays mounted at /workbench.

// The ported info pages link with plain hash anchors (#about, #send, …) —
// translate those clicks into router navigations so the ported files stay
// byte-comparable with the handoff source.
const HASH_ROUTES = {
  "#about": "/about",
  "#faq": "/faq",
  "#contact": "/contact",
  "#security": "/security",
  "#send": "/send",
  "#terms": "/terms",
  "#privacy": "/privacy",
  "#lookup": "/lookup",
  "#track": "/lookup",
  "#status": "/status",
  "#home": "/",
  "#privacy/deletion-request": "/privacy-deletion",
  // WS15 (2026-10-13): the ported info-page chrome also links Services and
  // the Mail guide by hash; without these two entries those clicks were
  // dead — the translator had no route for them (click-tested live).
  "#services": "/services",
  "#mail-guide": "/mail-guide",
};

const SITE_ORIGIN = "https://www.carecardexpress.com";

// True once the app has painted once, so focus is only moved on real navigations.
let initialLoadDone = false;

/** Keep head tags in step with the route. Titles alone were varying, so every
 *  page shared one description and one og:title — every shared link looked
 *  identical and search engines saw duplicate descriptions site-wide. */
function useDocumentHead({ title, description, path, robots = "index,follow" }) {
  React.useEffect(() => {
    document.title = title ? `${title} · CareCard Express` : "CareCard Express";

    const set = (selector, attr, value) => {
      if (!value) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        if (selector.includes("property=")) el.setAttribute("property", selector.match(/property="([^"]+)"/)[1]);
        else if (selector.includes("name=")) el.setAttribute("name", selector.match(/name="([^"]+)"/)[1]);
        else if (selector.includes("rel=")) el.setAttribute("rel", selector.match(/rel="([^"]+)"/)[1]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const fullTitle = title ? `${title} · CareCard Express` : "CareCard Express";
    set('meta[name="description"]', "content", description);
    set('meta[property="og:title"]', "content", fullTitle);
    set('meta[property="og:description"]', "content", description);
    set('meta[property="og:type"]', "content", "website");
    set('meta[name="twitter:card"]', "content", "summary");
    set('meta[name="twitter:title"]', "content", fullTitle);
    set('meta[name="twitter:description"]', "content", description);
    set('meta[name="robots"]', "content", robots);
    if (path) {
      set('link[rel="canonical"]', "href", SITE_ORIGIN + path);
      set('meta[property="og:url"]', "content", SITE_ORIGIN + path);
    }
  }, [title, description, path, robots]);
}

/** Publishes JSON-LD for the current route.
 *
 *  Deliberately renders nothing. React 19 hoists a <script> out of the tree
 *  into <head>, and the prerenderer captures the DOM *after* that hoist — so
 *  hydration found the tag already present and threw a mismatch (#418),
 *  discarding the prerendered markup it exists to preserve. Instead the value
 *  is published on window and scripts/prerender.js writes the tag into the
 *  static HTML, which is the only place a crawler reads it anyway. */
function StructuredData({ data }) {
  React.useEffect(() => {
    window.__STRUCTURED_DATA__ = data;
    return () => { delete window.__STRUCTURED_DATA__; };
  }, [data]);
  return null;
}

const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "CareCard Express",
  legalName: "CareCard Express LLC",
  url: SITE_ORIGIN,
  description:
    "Send a real photo postcard to someone held at a Broward County (BSO) detention facility. $3.99 flat, no account, printed and mailed via USPS.",
  areaServed: { "@type": "AdministrativeArea", name: "Broward County, Florida" },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "support@carecardexpress.com",
    availableLanguage: "English",
  },
};

function SkipLink() {
  const { t } = useFlowLocale();
  return <a className="skip-link" href="#main-content">{t.skipToContent || "Skip to content"}</a>;
}

function Shell({ children, wide, bare, title, description, robots }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const mainRef = React.useRef(null);
  const privateRoute = /^\/(?:admin|workbench|account|order|lookup|track|cancel|checkout|send|privacy-deletion)(?:\/|$)/.test(pathname);
  useDocumentHead({ title, description, path: pathname, robots: robots || (privateRoute ? "noindex,nofollow" : "index,follow") });

  // A client-side navigation leaves focus on <body>, so screen-reader users
  // get no signal that the page changed. Move it to the new main region.
  // The flag is module-scoped, not a ref: each route renders its own Shell, so
  // a per-instance "first render" check is true every single navigation.
  // preventScroll (WS11): the focus must not scroll — the browser's
  // focus-scroll landed every navigation ~130px down (the top of <main>,
  // below the utility strip + header), fighting the starts-at-top rule
  // that ScrollManager enforces. Focus still moves for assistive tech;
  // the scroll position is ScrollManager's job alone.
  React.useEffect(() => {
    if (initialLoadDone) mainRef.current?.focus({ preventScroll: true });
    initialLoadDone = true;
  }, [pathname]);

  const onClick = (e) => {
    const a = e.target.closest("a[href^='#']");
    if (!a) return;
    const to = HASH_ROUTES[a.getAttribute("href")];
    if (to) {
      e.preventDefault();
      navigate(to);
    }
  };
  // `wide` is the operator console and the design workbench: they are their
  // own environments and the marketing chrome would only get in the way.
  // `bare` is for pages that bring their own full-bleed layout.
  return (
    <div onClick={onClick} style={{ minHeight: "100vh", position: "relative" }}>
      <SkipLink />
      {/* Global controls on every customer page: language (EN/ES), theme,
          accent color, and accessibility (text size, contrast). */}
      {!wide && <ChromeControls />}
      {!wide && <SiteHeader isHome={pathname === "/"} inFlow={pathname.startsWith("/send")} />}
      <main id="main-content" ref={mainRef} tabIndex={-1}>
        <SiteFooterContext.Provider value={true}>
        {wide || bare ? children : <div className="app-shell">{children}</div>}
        </SiteFooterContext.Provider>
      </main>
      <SiteFooter />
    </div>
  );
}

function HomePage() {
  const navigate = useNavigate();
  return (
    <Shell bare title="Send a photo postcard to a Broward County facility — $3.99 flat" description="Send a real photo postcard to someone at a Broward County (BSO) jail. $3.99 flat, no account, printed and mailed via USPS. Track it with a 16-character order ID.">
      <StructuredData data={[ORGANIZATION_LD, {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Photo postcard to a Broward County detention facility",
        provider: { "@type": "Organization", name: "CareCard Express" },
        areaServed: { "@type": "AdministrativeArea", name: "Broward County, Florida" },
        offers: { "@type": "Offer", price: "3.99", priceCurrency: "USD",
                  availability: "https://schema.org/InStock" },
      }]}/>
      <LuxuryHome />
      <SupportWidget />
    </Shell>
  );
}

// ── Seasonal send-by page (/christmas) ────────────────────────────────────
// Harvest integration (2026-10-21): native production build of the fleet's
// ChristmasAd pattern — flagship hero, real sample photo, gold-only
// accents, date-driven Dec 11 send-by state (seasonal.jsx SEASON).
function ChristmasRoute() {
  return (
    <Shell bare title="Christmas postcards — send by Friday, December 11 · $3.99 flat" description="Send a real photo postcard for Christmas to a loved one at a Broward County facility. Mailrooms run slow in December — send by Friday, December 11 to get there before Christmas. $3.99 flat, printed and mailed via USPS.">
      <ChristmasPage />
      <SupportWidget />
    </Shell>
  );
}

// ── Per-facility landing pages (strategy §3/§9: the SEO spearhead and the
//    highest-converting entry — the visitor arrives with facility chosen). ──
function FacilityPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useDay25Locale();
  const facility = FACILITIES.find((f) => f.slug === slug);
  const [policy, setPolicy] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    if (facility) getGuestFacilityDetail(slug).then((value) => { if (live) setPolicy(value); });
    return () => { live = false; };
  }, [slug, facility]);
  if (!facility) return <Navigate to="/" replace />;
  const others = FACILITIES.filter((f) => f.slug !== slug);
  const blocked = policy ? policy?.orderable !== true : facility.slug === "paul-rein-detention";
  const unavailableReason = policy?.unavailable_reason || policy?.reason ||
    (blocked ? "Current facility-policy evidence is conflicted or not cleared for ordering." : null);
  const contradictions = policy?.open_contradictions || policy?.blockers || [];
  const start = () => {
    if (blocked) return;
    const sessionId = getGrowthSessionId();
    if (sessionId) captureGrowthEvent({
      event: "create_postcard_clicked", session_id: sessionId,
      facility_slug: facility.slug, event_key: `facility-create:${facility.slug}`,
      metadata: { surface: "facility-page" },
    }).catch(() => {});
    navigate("/send/create", { state: { facilitySlug: facility.slug } });
  };

  return (
    <Shell robots={blocked ? "noindex,follow" : "index,follow"} title={`Send Mail & Photos to ${facility.name}`} description={`Current CareCard availability and public mailing-policy information for ${facility.name} in Broward County.`}>
      <StructuredData data={[{
        "@context": "https://schema.org", "@type": "Service",
        name: `Send a photo postcard to ${facility.name}`, serviceType: "Photo postcard mailing service",
        provider: { "@type": "Organization", name: "CareCard Express", url: SITE_ORIGIN },
        areaServed: { "@type": "AdministrativeArea", name: "Broward County, Florida" },
        offers: { "@type": "Offer", price: "3.99", priceCurrency: "USD",
          availability: policy?.orderable === true ? "https://schema.org/InStock" : "https://schema.org/Discontinued",
          url: `${SITE_ORIGIN}/facilities/${facility.slug}` },
      }, {
        "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
          { "@type": "ListItem", position: 1, name: "Facilities", item: `${SITE_ORIGIN}/facilities` },
          { "@type": "ListItem", position: 2, name: facility.name, item: `${SITE_ORIGIN}/facilities/${facility.slug}` },
        ],
      }]}/>
      <div className="app-nav"><div className="app-logo"><div className="sym">C</div><div className="wordmark">Care<em>Card</em></div></div></div>
      <div style={{ padding: "28px 0 8px" }}>
        <div style={{ fontSize: 11, color: blocked ? "var(--warning)" : "var(--gold)", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 600, marginBottom: 14 }}>
          {blocked ? "Ordering unavailable" : "CareCard currently supports this facility"} · {facility.street}
        </div>
        <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: 36, lineHeight: 1.1, margin: "0 0 14px", color: "var(--ink-100)" }}>
          {facility.name}
        </h1>
        <p style={{ color: "var(--ink-300)", fontSize: 15, lineHeight: 1.55, maxWidth: 620 }}>
          {facility.blurb}. CareCard checks the active facility-policy record before an order can proceed.
          {blocked ? ` ${unavailableReason}` : " When available, CareCard formats the postcard using the active policy snapshot and revalidates before submission."}
        </p>
        <button className="btn btn-primary" disabled={blocked} style={{ minWidth: 260, fontSize: 15, marginBottom: 28 }} onClick={start}>
          {blocked ? "Sending unavailable" : `Send a card to ${facility.name} →`}
        </button>
      </div>

      <TrustConfidence surface="facility-page-trust" />
      <FacilityTrustCard policy={policy} facility={facility} />
      <p style={{margin: "0 0 22px"}}><a href="/broward-photo-postcard-guide" style={{color:"var(--gold)"}}>{t.guideLink} →</a></p>

      {contradictions.length > 0 && <div role="status" style={{ padding: 14, border: "1px solid var(--warning)", borderRadius: 10, marginBottom: 20 }}>
        Open contradictions: {contradictions.map((x) => typeof x === "string" ? x : (x.code || x.reason || "unresolved policy conflict")).join("; ")}
      </div>}

      <div className="wb-label" style={{ marginBottom: 12 }}>Useful public guidance</div>
      <div style={{ display: "grid", gap: 8, marginBottom: 28 }}>
        {[
          ["Photo postcards", "CareCard's current Broward flow is designed around photo postcards. Final acceptance depends on the active facility policy and physical/provider proof gates."],
          ["Recipient information", "Use current identifying and housing information. CareCard validates required fields against the active policy before the order can continue."],
          ["Outcome tracking", "Provider or postal progress is not treated as proof that the facility accepted the card or that the recipient received it."],
        ].map(([t, d], i) => <div key={i} style={{ padding: "13px 15px", background: "var(--ink-800)", border: "1px solid var(--ink-600)", borderRadius: 10 }}><div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-100)", marginBottom: 3 }}>{t}</div><div style={{ fontSize: 12.5, color: "var(--ink-300)", lineHeight: 1.5 }}>{d}</div></div>)}
      </div>

      <div className="wb-label" style={{ marginBottom: 12 }}>Other Broward facilities</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8, marginBottom: 36 }}>
        {others.map((f) => <button key={f.slug} onClick={() => navigate("/facilities/" + f.slug)} style={{ padding: "12px 14px", background: "var(--ink-800)", border: "1px solid var(--ink-600)", borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "var(--ink-200)", fontSize: 13, fontWeight: 600 }}>{f.name} <span style={{ color: "var(--gold)" }}>→</span></button>)}
      </div>
      <IndependentServiceDisclosure />
    </Shell>
  );
}

// Same wizard state as the workbench's GuestFlowSurface, mounted as a page.
function GuestFlowPage() {
  const { t } = useFlowLocale();
  const navigate = useNavigate();
  const { state: nav } = useLocation();
  // One-tap reorder from the dashboard writes a single-use payload to
  // sessionStorage ("carecard:reorder", see dashboard_shared.js
  // REORDER_STORAGE_KEY) and navigates to /send. Consume it here, then
  // remove it so a refresh doesn't resurrect a stale reorder. The photo URL
  // in the payload is preview-quality only, so the photo itself is always
  // re-selected in the flow; everything else pre-fills.
  const sessionReorder = React.useMemo(() => {
    try {
      const raw = sessionStorage.getItem("carecard:reorder");
      if (!raw) return null;
      const p = JSON.parse(raw);
      return p && typeof p === "object" ? p : null;
    } catch { return null; }
  }, []);
  const normalizedSessionReorder = React.useMemo(() => {
    if (!sessionReorder) return null;
    const fac = FACILITIES.find((f) => f.name === sessionReorder.facility);
    return {
      facilitySlug: fac ? fac.slug : null,
      recipientName: sessionReorder.recipient || "",
      arrestNumber: sessionReorder.booking_id || "",
      housingLocation: "",
      message: sessionReorder.message || "",
      design: sessionReorder.design || null,
    };
  }, [sessionReorder]);
  React.useEffect(() => {
    if (sessionReorder) {
      try { sessionStorage.removeItem("carecard:reorder"); } catch {}
    }
  }, [sessionReorder]);
  // Facility pages link in with the facility pre-chosen (strategy §4.1 ＋P1).
  const reorder = nav?.reorder || normalizedSessionReorder || null;
  const presetSlug = nav?.facilitySlug || reorder?.facilitySlug;
  const preset = presetSlug && FACILITIES.find((f) => f.slug === presetSlug);
  // WEB-02 gives every step its own route, so the URL is the single source of
  // truth for where the customer is. Back, Forward, a refresh and a shared
  // link then all agree, which they could not when the step lived in component
  // state plus a localStorage mirror.
  const location = useLocation();
  const { stepKey } = useParams();
  const step = React.useMemo(() => {
    // Canonical step paths first (PATH_TO_STEP, flow_steps.js); old deep
    // links and the old canonical stage paths resolve into the step that now
    // owns them.
    const direct = PATH_TO_STEP[location.pathname];
    if (direct !== undefined) return direct;
    const legacy = stepKey ? LEGACY_STEP_TO_STAGE[stepKey] : undefined;
    return legacy === undefined ? 0 : legacy;
  }, [location.pathname, stepKey]);
  const goStep = React.useCallback(
    (i) => navigate(FLOW_STEPS[Math.max(0, Math.min(FLOW_STEPS.length - 1, i))].path),
    [navigate]);

  // Start empty. These previously defaulted to SAMPLE data, so a real sender
  // opened the flow with a stranger's name, a fictional arrest number and
  // somebody else's message already filled in — confusing at best, and a
  // wrong-recipient order at worst. Sample data belongs to the design
  // workbench, not the live flow.
  const [state, setState] = React.useState({
    facility: preset || null,
    name: reorder?.recipientName || "",
    arrest: reorder?.arrestNumber || "",
    // Required: BSO's 2025 handbook lists the housing location among the
    // things incoming mail must carry, and the mailroom routes on it.
    housing: reorder?.housingLocation || "",
    image: null,          // { id, url, local? } once a photo is uploaded
    message: reorder?.message || "",
    designId: reorder?.design || "classic",
    // Return address — required on every order (owner decision
    // 2026-10-31, §20.27 BUILD-27). Since the owner decision of
    // 2026-10-29 it prints on the card as the sender's own return
    // address, and CareCard does not keep it after mailing (B-02,
    // §20.24 BUILD-24).
    sender: { ...EMPTY_SENDER },
    ack: false,
    // Separate from `ack` on purpose. The general rules acknowledgment and the
    // photo attestation are different statements, and the server treats them
    // as different fields.
    likenessAttested: false,
    housingConfirmed: true,
  });
  const upd = (p) => setState((s) => ({ ...s, ...p }));

  // Delivery scheduling + recurring sends, lifted into flow state so the
  // review-stage picker and the checkout payload share one source of truth.
  // Both mirror to localStorage (cc-scheduled / cc-recurrence); the order
  // payload below prefers state with a storage fallback.
  const [scheduledFor, setScheduledFor] = React.useState(() => {
    try { return localStorage.getItem("cc-scheduled") || null; } catch { return null; }
  });
  const [recurrence, setRecurrence] = React.useState(() => {
    try {
      const raw = localStorage.getItem("cc-recurrence");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // Ask before rendering a checkout the server would refuse. Letting somebody
  // choose a facility, type a message to a person in custody and upload a
  // family photo — then 503-ing them at the pay button — is a worse outage
  // than a notice up front.
  //
  // flags === null after loading means no backend (static demo): show the
  // flow rather than inventing an outage. While the flags are still loading,
  // render a fixed-height placeholder in the same container the paused notice
  // uses — the first paint used to show the full flow and then swap to the
  // notice once the flags arrived, a whole-page layout shift (CLS).
  const [flags, setFlags] = React.useState(null);
  const [flagsLoading, setFlagsLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    getPublicFlags().then((f) => {
      if (cancelled) return;
      setFlags(f);
      setFlagsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);
  const checkoutOff = flags && (!flags.guest_checkout_enabled || !flags.uploads_enabled || !flags.payment_checkout_available);
  // payment_enabled=false in this build: the checkout step renders as a
  // clearly labeled demo (ScreenPay disables the pay button when this is
  // false). No real payment path exists either way.
  const paymentLive = Boolean(flags && flags.payment_checkout_available);
  const [payError, setPayError] = React.useState(null);

  // First-party funnel telemetry contains only an opaque session id, step name
  // and facility slug. Recipient/message/photo/email content never enters it.
  const isReorder = Boolean(reorder);
  React.useEffect(() => {
    const sessionId = getGrowthSessionId();
    if (!sessionId) return;
    const event = step === 0 ? "send_started" : step === FLOW_STEPS.length - 1 ? "checkout_started" : null;
    if (!event) return;
    captureGrowthEvent({
      event, session_id: sessionId, event_key: event,
      facility_slug: state.facility?.slug || undefined,
      metadata: isReorder ? { is_reorder: "true" } : {},
    }).catch(() => {});
    recordDay25ExperimentConversion(event);
  }, [step, state.facility?.slug, isReorder]);

  // How far the draft actually reaches. A step is reachable only when every
  // step before it is satisfied, so a deep link to /checkout on an empty draft
  // lands on the facility step rather than on a checkout for nothing.
  //
  // This is computed from the in-memory draft and nothing else, via the pure
  // stepComplete/firstIncompleteStep helpers (flow_steps.js) that mirror
  // §5.1–§5.6. The sensitive draft is deliberately not persisted (§4.5), so
  // a refresh resolves back to the first step — which is the correct
  // behaviour, not a bug: an unsent message, recipient and photo must not
  // survive a reload.
  const firstIncomplete = React.useMemo(() => firstIncompleteStep(state), [state]);

  React.useEffect(() => {
    if (payError || checkoutOff) return;
    // Bare /send is the canonical entry: resolve it to the step the draft has
    // actually reached rather than leaving the customer on a URL that is not a
    // step. Anything past the first incomplete step falls back to that step.
    const target = Math.min(step, firstIncomplete);
    const bareEntry = location.pathname === "/send" || location.pathname === "/send/";
    if (bareEntry || target !== step) {
      navigate(FLOW_STEPS[bareEntry ? firstIncomplete : target].path, { replace: true });
    }
  }, [step, firstIncomplete, payError, checkoutOff, navigate, location.pathname]);

  if (payError) {
    return (
      <Shell>
        <ScreenPaymentFailed
          error={payError}
          onRetry={() => setPayError(null)}
          onBack={() => { setPayError(null); goStep(2); }}
        />
      </Shell>
    );
  }

  if (flagsLoading) {
    return (
      <Shell title="Send a postcard" description="Send a printed photo postcard to a person in a Broward County detention facility.">
        <div className="mod-empty" style={{ paddingTop: 48, minHeight: 360 }}>
          <div className="mod-empty-h" role="status">Checking availability…</div>
        </div>
      </Shell>
    );
  }

  if (checkoutOff) {
    return (
      <Shell title={t.pausedTitle} description={t.pausedDesc}>
        <div className="mod-empty" style={{ paddingTop: 48, minHeight: 360 }}>
          <I.Alert size={30}/>
          <div className="mod-empty-h">{t.pausedHead}</div>
          <p className="mod-empty-p">
            {t.pausedBody}
          </p>
          <div style={{display: 'flex', gap: 10, marginTop: 20, justifyContent: 'center', flexWrap: 'wrap'}}>
            {/* WS15: the waitlist is the paused state's whole point, yet no
                page linked to /waitlist at all — it was an orphan route.
                It is the primary action here now. */}
            <button className="btn btn-primary" onClick={() => navigate("/waitlist")}>{t.pausedWaitlist}</button>
            <button className="btn btn-ghost" onClick={() => navigate("/lookup")}>{t.noOrderTrack}</button>
            <button className="btn btn-ghost" onClick={() => navigate("/contact")}>{t.pausedContact}</button>
          </div>
        </div>
      </Shell>
    );
  }

  const screen = (() => {
    switch (FLOW_STEPS[step].k) {
      case "facility":
        return <StepFacility facility={state.facility} setFacility={(f) => upd({ facility: f })} onBack={() => navigate("/")} onNext={() => goStep(1)} />;
      case "recipient":
        return <StepRecipient facility={state.facility} setFacility={(f) => upd({ facility: f })} name={state.name} setName={(v) => upd({ name: v })} arrest={state.arrest} setArrest={(v) => upd({ arrest: v })} housing={state.housing} setHousing={(v) => upd({ housing: v })} sender={state.sender} setSender={(v) => upd({ sender: v })} onBack={() => goStep(0)} onNext={() => goStep(2)} />;
      case "photo":
        return <StepPhoto image={state.image} setImage={(v) => upd({ image: v })} name={state.name} onBack={() => goStep(1)} onNext={() => goStep(3)} />;
      case "message":
        return <StepMessage message={state.message} setMessage={(v) => upd({ message: v })} image={state.image} designId={state.designId} setDesignId={(v) => upd({ designId: v })} onBack={() => goStep(2)} onNext={() => goStep(4)} />;
      case "rules":
        return <StepRules facility={state.facility} name={state.name} ack={state.ack} setAck={(v) => upd({ ack: v })} likeness={state.likenessAttested} setLikeness={(v) => upd({ likenessAttested: v })} onBack={() => goStep(3)} onNext={() => goStep(5)} />;
      case "review":
        return <StepReview facility={state.facility} name={state.name} arrest={state.arrest} housing={state.housing} message={state.message} imageUrl={state.image?.url} sender={state.sender} scheduledFor={scheduledFor} setScheduledFor={setScheduledFor} recurrence={recurrence} setRecurrence={setRecurrence} onBack={() => goStep(4)} onNext={() => {
          const sessionId = getGrowthSessionId();
          if (sessionId) captureGrowthEvent({ event: "review_completed", session_id: sessionId, event_key: "review_completed", facility_slug: state.facility?.slug || undefined, metadata: { surface: "review" } }).catch(() => {});
          goStep(6);
        }} />;
      case "checkout":
      default:
        return (
          <ScreenPay
            flowNav
            facility={state.facility}
            name={state.name}
            // payment_enabled=false: checkout renders as a clearly labeled
            // demo — the $3.99 total is shown, the pay button is disabled, and
            // onPay can never fire.
            paymentLive={paymentLive}
            onBack={() => goStep(5)}
            onPay={async ({ email, termsAccepted }) => {
              let order = null;
              try {
                order = await createGuestOrder({
                  facility_slug: state.facility.slug,
                  recipient_name: state.name,
                  arrest_number: state.arrest,
                  housing_location: state.housing,
                  message: state.message,
                  image_id: state.image?.id || null,
                  // PHOTO-INMATE-LIKENESS-01 / BSO-HOUSING-01. Both are the
                  // customer's explicit statement, sent as their own fields
                  // rather than folded into the general rules acknowledgment —
                  // the server refuses the order without them, and a sender who
                  // says the photo shows someone in custody is refused rather
                  // than queued for a reviewer to catch.
                  likeness_attested: state.likenessAttested === true,
                  housing_confirmed: state.housingConfirmed !== false,
                  email,
                  promo_code: null,
                  scheduled_for: localStorage.getItem("cc-scheduled") || null,
                  // Recurring sends: null = one-off. The backend creates a
                  // series + fulfillment instances when a rule is present.
                  recurrence: (() => {
                    try { return recurrence || JSON.parse(localStorage.getItem("cc-recurrence") || "null"); }
                    catch { return recurrence || null; }
                  })(),
                  attribution_session_id: getGrowthSessionId() || null,
                  is_reorder: Boolean(reorder),
                  terms_accepted: termsAccepted === true,
                  // Optional. Sent only when the customer actually gave one,
                  // so an empty form does not post five empty strings the
                  // server would then have to decide about.
                  ...(state.sender.line1 ? {
                    sender_name: state.sender.name,
                    sender_line1: state.sender.line1,
                    sender_line2: state.sender.line2 || null,
                    sender_city: state.sender.city,
                    sender_state: state.sender.state,
                    sender_zip: state.sender.zip,
                  } : {}),
                });
              } catch (e) {
                // Every failure stops here. This used to fall through to the
                // design's sample confirmation whenever the backend was
                // unreachable, which meant a timeout or an offline phone
                // showed the customer "Order confirmed" and a made-up order
                // id for a card that did not exist. A confirmation screen is
                // a claim about the world; it is only allowed when the server
                // actually answered with an order.
                setPayError(e);
                return;
              }
              const checkoutUrl = order?.checkout?.url;
              if (!checkoutUrl) {
                setPayError({ code: "PAYMENT_HANDOFF_MISSING", message: "Secure Checkout was not created. Nothing has been marked paid." });
                return;
              }
              localStorage.removeItem("cc-scheduled");
              try { localStorage.removeItem("cc-recurrence"); } catch {}
              // Creating the order is not payment. Leave CareCard for Stripe's
              // hosted Checkout and only render confirmation after the return
              // endpoint retrieves/verifies the Session as PAID.
              window.location.assign(checkoutUrl);
            }}
          />
        );
    }
  })();

  return <Shell title="Send a postcard" description="Choose a facility, add a photo and message, and we print and mail a 4x6 postcard to a Broward County detention facility for $3.99.">{screen}</Shell>;
}

function CheckoutSuccessPage() {
  const { t } = useFlowLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = React.useMemo(() => new URLSearchParams(location.search).get("session_id"), [location.search]);
  const [result, setResult] = React.useState({ state: "loading" });

  React.useEffect(() => {
    if (!sessionId || !sessionId.startsWith("cs_")) {
      setResult({ state: "error", error: { code: "PAYMENT_SESSION_MISSING", message: "The Stripe return did not include a valid Checkout Session." } });
      return;
    }
    let alive = true;
    reconcileGuestCheckout(sessionId)
      .then((order) => alive && setResult({ state: order?.state?.payment === "PAID" ? "paid" : "pending", order }))
      .catch((error) => alive && setResult({ state: "error", error }));
    return () => { alive = false; };
  }, [sessionId]);

  if (result.state === "loading") {
    return <Shell title={t.confirmingTitle}><div role="status" aria-live="polite" className="mod-empty" style={{paddingTop:48}}><div className="mod-empty-h">{t.confirmingHead}</div><p className="mod-empty-p">{t.confirmingBody}</p></div></Shell>;
  }
  if (result.state === "pending") {
    return <Shell title={t.processingTitle}><div className="mod-empty" style={{paddingTop:48}}><I.Clock size={30}/><div className="mod-empty-h">{t.processingHead}</div><p className="mod-empty-p">{t.processingBody}</p><button className="btn btn-primary" onClick={() => navigate(`/${result.order.public_id}`)}>{t.successTrack}</button></div></Shell>;
  }
  if (result.state === "error") {
    return <Shell><ScreenPaymentFailed error={result.error} onRetry={() => window.location.reload()} onBack={() => navigate("/send/checkout")} /></Shell>;
  }
  const order = result.order;
  return (
    <Shell title={t.successPaidLabel}>
      <ScreenSuccess
        publicId={order.public_id}
        facility={{ name: order.facility_name }}
        name={order.recipient_name}
        moderationState={order.moderation_state}
        totalCents={order.total_cents}
        onLookup={() => navigate(`/${order.public_id}`)}
      />
    </Shell>
  );
}

function CheckoutCancelPage() {
  const { t } = useFlowLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const orderId = new URLSearchParams(location.search).get("order");
  return (
    <Shell title={t.cancelTitle}>
      <div className="mod-empty" style={{paddingTop:48}}>
        <I.Alert size={30}/>
        <div className="mod-empty-h">{t.leftPaymentHead}</div>
        <p className="mod-empty-p">
          {t.cancelBody}
        </p>
        {orderId && <div style={{fontFamily:'var(--cc-mono)', fontSize:12, marginBottom:16}}>{t.successOrderLabel} {orderId}</div>}
        <div style={{display:'flex', gap:10, justifyContent:'center'}}>
          <button className="btn btn-ghost" onClick={() => navigate("/lookup")}>{t.successTrack}</button>
          <button className="btn btn-primary" onClick={() => navigate("/send")}>{t.leftPaymentRetry}</button>
        </div>
      </div>
    </Shell>
  );
}

function SentPage() {
  const { t } = useFlowLocale();
  const navigate = useNavigate();
  const { state } = useLocation();
  const order = state?.order;

  // /sent is reachable by typing the URL, by a reload that drops navigation
  // state, and by the back button. None of those is an order. A confirmation
  // rendered from sample data would be a claim we cannot support, so this
  // says plainly that there is nothing to confirm.
  if (!order?.public_id) {
    return (
      <Shell title={t.noOrderTitle}>
        <div className="mod-empty" style={{ paddingTop: 48 }}>
          <div className="mod-empty-h">{t.noOrderHead}</div>
          <p className="mod-empty-p">
            {t.noOrderBody}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn btn-ghost" onClick={() => navigate("/lookup")}>{t.noOrderTrack}</button>
            <button className="btn btn-primary" onClick={() => navigate("/send")}>{t.noOrderBack}</button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title={t.sentConfirmedTitle}>
      <ScreenSuccess
        publicId={order.public_id}
        facility={state?.facility || SAMPLE.facility}
        name={state?.name || SAMPLE.recipientName}
        email={state?.email}
        moderationState={order?.moderation_state}
        onLookup={() => navigate("/lookup")}
      />
    </Shell>
  );
}

function LookupPage() {
  const { t } = useFlowLocale();
  const navigate = useNavigate();
  return (
    <Shell title={t.lookupPageTitle} description={t.lookupPageDesc}>
      <OrderLookup onFound={(id) => id && navigate("/" + id.toUpperCase())} />
    </Shell>
  );
}

// Picking a date stores it for the current draft; the payment step sends it
// as scheduled_for and the order's fulfillment waits until that date.
function SchedulePage() {
  const navigate = useNavigate();
  return (
    <Shell title="Schedule a send">
      <ScheduledSendPicker
        onBack={() => navigate("/send")}
        onConfirm={(date) => {
          if (date) localStorage.setItem("cc-scheduled", date);
          else localStorage.removeItem("cc-scheduled");
          navigate("/send");
        }}
      />
    </Shell>
  );
}

function CancelOrderPage() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const valid = isLookupOrderId(publicId);
  return (
    <Shell>
      {valid ? (
        <CancelOrder
          publicId={publicId.toUpperCase()}
          onConfirm={async (email) => {
            try {
              return await cancelGuestOrder(publicId.toUpperCase(), email);
            } catch (e) {
              // 4xx = the backend refused (wrong email / too late) — surface it.
              // Backend unreachable (static demo) keeps the sample behavior.
              return !(e.status >= 400);
            }
          }}
          onCancel={() => navigate("/" + publicId.toUpperCase())}
        />
      ) : (
        <OrderNotFound onRetry={() => navigate("/lookup")} />
      )}
    </Shell>
  );
}

// carecard.express/<order id> — current 16-character IDs and exact legacy IDs resolve; anything else 404s.
// Real orders come from the guest API; when the backend is unreachable (static
// deploy of just the frontend) any well-formed id falls back to the design's
// detailed sample tracking screen.
function TrackingPage() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const valid = isLookupOrderId(publicId);
  const id = (publicId || "").toUpperCase();
  const [result, setResult] = React.useState({ state: "loading" });

  React.useEffect(() => {
    if (!valid) return;
    let alive = true;
    getGuestOrder(id)
      .then((order) => alive && setResult({ state: "found", order }))
      .catch((e) => alive && setResult({ state: e.status === 404 ? "missing" : "offline" }));
    return () => { alive = false; };
  }, [id, valid]);

  // A path that is not an order id is simply a page that does not exist.
  if (!valid) return <NotFoundPage />;
  if (result.state === "missing") {
    return <Shell><OrderNotFound publicId={id} onRetry={() => navigate("/lookup")} /></Shell>;
  }
  if (result.state === "loading") {
    return (
      <Shell>
        <div role="status" aria-live="polite" style={{ minHeight: "60vh", display: "grid", placeItems: "center", color: "var(--ink-300)", fontSize: 14 }}>
          Looking up order <span style={{ fontFamily: 'var(--cc-mono)', color: "var(--ink-200)", marginLeft: 6 }}>{id}</span>…
        </div>
      </Shell>
    );
  }
  if (result.state === "found") {
    // §5.9: the public tracking surface renders ONLY the governed minimal
    // public fields — never facility identity/address, recipient name,
    // arrest/CIS number, housing, email, message, image or account data.
    // TRUTHFUL-TRACKING-02: the two truthfulness flags the same payload
    // carries are passed through with the order, so the card labels the
    // projected fulfilment steps as expected until the server reports
    // provider-backed tracking. A flag the API returns and the page ignores
    // is the same as no flag.
    const o = result.order;
    return (
      <Shell>
        <PublicTrackingCard
          order={o}
          fulfillmentTracked={o.fulfillment_tracked}
          printSubmitted={o.print_submitted}
          onCancel={() => navigate(`/cancel/${id}`)}
        />
      </Shell>
    );
  }
  // No backend (static deploy of just the frontend): render the minimal public
  // card over an explicitly development-marked sample rather than inventing
  // recipient/facility detail we do not have.
  return (
    <Shell>
      <PublicTrackingCard
        order={{
          public_id: id,
          display_id: id,
          status: "IN_REVIEW",
          payment_state: "NOT_CHARGED",
          price_cents: 399,
          scheduled_for: null,
          created_at: new Date().toISOString(),
          canceled_at: null,
          cancel_available: false,
          timeline: [{ status: "IN_REVIEW", at: new Date().toISOString() }],
          development_only: true,
        }}
        onCancel={() => navigate("/lookup")}
      />
    </Shell>
  );
}

function WaitlistRoute() {
  const [liveCount, setLiveCount] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    getWaitlistCount().then((r) => alive && setLiveCount(r.count)).catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <Shell title="Join the waitlist">
      <WaitlistPage liveCount={liveCount} onJoin={(email) => joinGuestWaitlist(email).catch(() => {})} />
    </Shell>
  );
}

function AttributionTracker() {
  const location = useLocation();
  React.useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const tracked = ["utm_source","utm_medium","utm_campaign","utm_content","utm_term","source","campaign","partner","ref"];
    const hasTouch = tracked.some((key) => params.has(key));
    const existing = getGrowthSessionId();
    const facilityNavigation = location.pathname.startsWith("/facilities/");
    if (existing && !hasTouch && !facilityNavigation) return;

    const payload = {
      session_id: existing || undefined,
      source: params.get("utm_source") || params.get("source") || undefined,
      medium: params.get("utm_medium") || undefined,
      campaign: params.get("utm_campaign") || params.get("campaign") || undefined,
      content: params.get("utm_content") || undefined,
      term: params.get("utm_term") || undefined,
      landing_page: location.pathname,
      facility_page: location.pathname.startsWith("/facilities/") ? location.pathname : undefined,
      partner_code: params.get("partner") || undefined,
      referral_code: params.get("ref") || undefined,
    };
    let active = true;
    captureAttribution(payload).then((row) => {
      if (!active || !row?.session_id) return;
      setGrowthSessionId(row.session_id);
      const qualified = captureGrowthEvent({
        event: "qualified_visit", session_id: row.session_id,
        event_key: "qualified_visit", facility_slug: undefined,
      }).catch(() => {});
      const source = (params.get("utm_source") || params.get("source") || "").toLowerCase();
      const channelEvent = source === "community" ? "community_visit" : source === "partner" ? "partner_visit" : null;
      if (channelEvent) captureGrowthEvent({
        event: channelEvent, session_id: row.session_id, event_key: channelEvent,
        metadata: { surface: "day25-share-link" },
      }).catch(() => {});
      return qualified;
    }).catch(() => {});

    if (hasTouch && typeof window !== "undefined") {
      const clean = new URL(window.location.href);
      tracked.forEach((key) => clean.searchParams.delete(key));
      window.history.replaceState(window.history.state, "", `${clean.pathname}${clean.search}${clean.hash}`);
    }
    return () => { active = false; };
  }, [location.pathname, location.search]);
  return null;
}


function CreatePostcardClickTracker() {
  const location = useLocation();
  React.useEffect(() => {
    const handler = (event) => {
      const link = event.target?.closest?.('a[href^="/send"]');
      if (!link) return;
      const sessionId = getGrowthSessionId();
      if (!sessionId) return;
      captureGrowthEvent({
        event: "create_postcard_clicked", session_id: sessionId,
        event_key: `create:${location.pathname}`.slice(0, 160),
        metadata: { surface: "create-postcard-link" },
      }).catch(() => {});
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [location.pathname]);
  return null;
}

// B-32: dedicated 404 page. The catch-all route used to Navigate to "/"
// silently — a typo'd link looked like the site just refused to go anywhere,
// and nothing told the visitor what happened. This renders a branded page
// instead. The order-level OrderNotFound (final_additions.jsx) is untouched;
// this is the site-level "no such page".
const NOTFOUND_COPY = {
  en: { heading: "This page doesn't exist.", sub: "The link may be broken, or the page was removed." },
  es: { heading: "Esta página no existe.", sub: "Es posible que el enlace esté roto o que la página haya sido eliminada." },
  ht: { heading: "Paj sa a pa egziste.", sub: "Lyen an ka kase, oswa yo te retire paj la." },
};

function NotFoundPage() {
  const { t } = useFlowLocale();
  return (
    <Shell bare title="Page not found" description="CareCard Express — the page you're looking for doesn't exist.">
      <div style={{ padding: "56px 0 24px", textAlign: "center" }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: "var(--ink-800)", border: "1px dashed var(--gold)",
          color: "var(--gold)",
          display: "grid", placeItems: "center",
          margin: "0 auto 24px",
          fontSize: 26, fontWeight: 700, fontFamily: "\"JetBrains Mono\",monospace",
        }}>
          404
        </div>

        {Object.entries(NOTFOUND_COPY).map(([lang, copy]) => (
          <React.Fragment key={lang}>
            <h1 className="screen-title" lang={lang}
                style={lang === "en" ? { fontSize: 26 } : { fontSize: 16, marginTop: 16, color: "var(--ink-200)" }}>
              {copy.heading}
            </h1>
            {lang === "en" && <p className="screen-sub">{copy.sub}</p>}
            {lang !== "en" && <p style={{ fontSize: 13, color: "var(--ink-400)", marginTop: 4 }}>{copy.sub}</p>}
          </React.Fragment>
        ))}
      </div>

      <div style={{
        display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap",
        paddingBottom: 56,
      }}>
        {[
          { to: "/", label: t.notfoundHome },
          { to: "/track", label: t.noOrderTrack },
          { to: "/help", label: t.trackingHelp },
        ].map((link) => (
          <Link key={link.to} to={link.to} style={{
            padding: "10px 20px", borderRadius: 8,
            border: "1px solid var(--gold)", color: "var(--gold)",
            textDecoration: "none", fontSize: 14, fontWeight: 600,
          }}>
            {link.label}
          </Link>
        ))}
      </div>
    </Shell>
  );
}

// WEB-03 §15.3: /account/orders/:id is the protected owned-order detail
// route. The detail itself is served by the privacy-minimized tracking page
// for the order's public id (and by /order/:orderId when a capability
// fragment is presented), so the account deep link resolves there instead
// of duplicating an order-detail surface.
function AccountOrderDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/${(id || "").toUpperCase()}`} replace />;
}

// STARTS AT TOP (owner direction, 2026-10-13): every navigation — first
// load, route change, back/forward — starts at the top of the new page.
// Browser scroll restoration is disabled so a long page never reopens
// mid-scroll. The one exception is an intentional in-page anchor: when the
// new location carries a fragment naming an element on the page (the
// #cc-main skip link is the canonical one), that element is scrolled into
// view instead. A fragment naming no element — the order capability
// fragment is one — falls through to the top and is left untouched.
function ScrollManager() {
  const location = useLocation();
  React.useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);
  React.useEffect(() => {
    const id = location.hash ? location.hash.slice(1) : "";
    if (id) {
      let el = null;
      try { el = document.getElementById(id); } catch { el = null; }
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.search, location.hash]);
  return null;
}

// BACK TO TOP (owner direction, 2026-10-13): a branded control — gold arrow
// on black, circular — that appears once the reader has scrolled, floats
// above the mobile sticky CTA bar, smooth-scrolls home, and hides again at
// the top. Its colours are pinned brand values (chrome.css), never theme
// variables, so no theme flip can wash it out.
function BackToTop() {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const toTop = () => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
  };
  return (
    <button
      type="button"
      className={`cc-back-to-top${visible ? " is-visible" : ""}`}
      aria-label="Back to top"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      onClick={toTop}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function Site() {
  useTheme(); // applies the persisted Light/Dark/System choice to <html data-theme>
  const navigate = useNavigate();

  // WS15 (2026-10-13): the hash-click translator used to live only inside
  // Shell, so surfaces rendered without Shell (the /workbench design
  // surfaces) had dead hash links — click-tested live, every one of them
  // went nowhere. This document-level listener gives every route the same
  // translation. Shell's own handler runs first (it is closer to the
  // target) and calls preventDefault, so when this listener sees an
  // already-prevented event it stands down — no double navigation.
  // Modified clicks (new tab/window) keep their native behaviour, and a
  // hash naming an in-page element rather than a route is untouched:
  // HASH_ROUTES has no entry for it, so the browser default proceeds.
  React.useEffect(() => {
    const onHashRouteClick = (e) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target && e.target.closest ? e.target.closest("a[href^='#']") : null;
      if (!a) return;
      const to = HASH_ROUTES[a.getAttribute("href")];
      if (to) {
        e.preventDefault();
        navigate(to);
      }
    };
    document.addEventListener("click", onHashRouteClick);
    return () => document.removeEventListener("click", onHashRouteClick);
  }, [navigate]);

  return (

    <>
    <ScrollManager />
    <BackToTop />
    <AttributionTracker />
    <React.Suspense fallback={<ChunkLoading />}>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/christmas" element={<ChristmasRoute />} />
      <Route path="/giveaway" element={<Shell title="Free postcard giveaway" description="CareCard Express is giving the first 10 postcards in this campaign away free. No payment step, no account — check whether free cards are left and send one."><GiveawayPage /></Shell>} />
      <Route path="/send" element={<GuestFlowPage />} />
      <Route path="/send/:stepKey" element={<GuestFlowPage />} />
      <Route path="/checkout" element={<GuestFlowPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
      <Route path="/sent" element={<SentPage />} />
      {/* WEB-02 names /order-confirmation and /track. The older /sent and
          /lookup paths keep working so existing links and emails do not break,
          and both render the same page. */}
      <Route path="/order-confirmation" element={<SentPage />} />
      <Route path="/lookup" element={<LookupPage />} />
      <Route path="/track" element={<LookupPage />} />
      <Route path="/about" element={<Shell title="About" description="Who runs CareCard Express, how the $3.99 postcard works, and what we will not do with your data."><AboutPage /></Shell>} />
      <Route path="/faq" element={
        <Shell title="FAQ" description="How long delivery takes, what BSO mail rules allow, refunds, cancellations, and whether you need an account.">
          <StructuredData data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_SECTIONS.flatMap((sec) => sec.items.map((it) => ({
              "@type": "Question",
              name: it.q,
              acceptedAnswer: { "@type": "Answer", text: it.a },
            }))),
          }}/>
          <FAQPage />
        </Shell>
      } />
      <Route path="/contact" element={<Shell title="Contact" description="Reach CareCard Express support about an order, a refund, a delivery issue, or a moderation decision."><ContactPage onSubmit={(form) => submitContact(form).catch(() => {})} /></Shell>} />
      <Route path="/security" element={<Shell title="Security" description="Responsible disclosure policy and security.txt for CareCard Express."><SecurityPage /></Shell>} />
      <Route path="/terms" element={<Shell title="Terms of Service" description="The terms that apply when you order a postcard through CareCard Express."><LegalPage kind="terms" /></Shell>} />
      <Route path="/privacy" element={<Shell title="Privacy Policy" description="What CareCard Express collects, how long it is kept, and how to request deletion."><LegalPage kind="privacy" /></Shell>} />
      <Route path="/status" element={<Shell title="System Status" description="Live operational status for CareCard Express printing, mailing, and tracking."><PublicStatusPage /></Shell>} />
      <Route path="/waitlist" element={<WaitlistRoute />} />
      <Route path="/schedule" element={<SchedulePage />} />
      <Route path="/cancel/:publicId" element={<CancelOrderPage />} />
      <Route path="/privacy-deletion" element={<Shell title="Privacy Deletion Request"><PrivacyDeletion /></Shell>} />
      <Route path="/privacy-deletion/confirm" element={<Shell title="Deletion Confirmed"><DeletionConfirmPage /></Shell>} />
      <Route path="/admin/login" element={<Shell wide title="Admin Login"><AdminLogin /></Shell>} />
      <Route path="/admin" element={<Shell wide title="Admin"><AdminDashboard /></Shell>} />
      <Route path="/admin/giveaway" element={<Shell wide title="Admin · Giveaway"><AdminGiveawayRoute /></Shell>} />
      {/* WEB-08 §15.8: every required admin surface gets its own route. */}
      <Route path="/admin/orders" element={<Shell wide title="Admin · Orders"><AdminOrdersRoute /></Shell>} />
      <Route path="/admin/orders/review" element={<Shell wide title="Admin · Order review"><AdminOrdersReviewRoute /></Shell>} />
      <Route path="/admin/orders/failed" element={<Shell wide title="Admin · Failed orders"><AdminOrdersFailedRoute /></Shell>} />
      <Route path="/admin/orders/cancellations" element={<Shell wide title="Admin · Cancellations"><AdminOrdersCancellationsRoute /></Shell>} />
      <Route path="/admin/orders/refunds" element={<Shell wide title="Admin · Refunds"><AdminOrdersRefundsRoute /></Shell>} />
      <Route path="/admin/orders/:id" element={<Shell wide title="Admin · Order"><AdminOrderDetailRoute /></Shell>} />
      <Route path="/admin/compliance" element={<Shell wide title="Admin · Compliance"><AdminComplianceRoute /></Shell>} />
      <Route path="/admin/compliance/review" element={<Shell wide title="Admin · Compliance review"><AdminComplianceRoute /></Shell>} />
      <Route path="/admin/facilities" element={<Shell wide title="Admin · Facilities"><AdminFacilitiesRoute /></Shell>} />
      <Route path="/admin/facilities/:id/rules" element={<Shell wide title="Admin · Facility rules"><AdminFacilityRulesRoute /></Shell>} />
      <Route path="/admin/facilities/:id" element={<Shell wide title="Admin · Facility"><AdminFacilitiesRoute /></Shell>} />
      <Route path="/admin/customers" element={<Shell wide title="Admin · Customers"><AdminCustomersRoute /></Shell>} />
      <Route path="/admin/customers/:id" element={<Shell wide title="Admin · Customer"><AdminCustomersRoute /></Shell>} />
      <Route path="/admin/payments" element={<Shell wide title="Admin · Payments"><AdminPaymentsRoute /></Shell>} />
      <Route path="/admin/providers" element={<Shell wide title="Admin · Providers"><AdminProvidersRoute /></Shell>} />
      <Route path="/admin/providers/:provider" element={<Shell wide title="Admin · Provider"><AdminProviderDetailRoute /></Shell>} />
      <Route path="/admin/webhooks" element={<Shell wide title="Admin · Webhooks"><AdminWebhooksRoute /></Shell>} />
      <Route path="/admin/jobs" element={<Shell wide title="Admin · Jobs"><AdminJobsRoute /></Shell>} />
      <Route path="/admin/system-health" element={<Shell wide title="Admin · System health"><AdminSystemHealthRoute /></Shell>} />
      <Route path="/admin/alerts" element={<Shell wide title="Admin · Alerts"><AdminAlertsRoute /></Shell>} />
      <Route path="/admin/audit-log" element={<Shell wide title="Admin · Audit log"><AdminAuditLogRoute /></Shell>} />
      <Route path="/admin/admin-users" element={<Shell wide title="Admin · Admin users"><AdminUsersRoute /></Shell>} />
      <Route path="/admin/roles" element={<Shell wide title="Admin · Roles"><AdminRolesRoute /></Shell>} />
      <Route path="/admin/security" element={<Shell wide title="Admin · Security"><AdminSecurityRoute /></Shell>} />
      <Route path="/admin/feature-flags" element={<Shell wide title="Admin · Feature flags"><AdminFeatureFlagsRoute /></Shell>} />
      <Route path="/admin/maintenance" element={<Shell wide title="Admin · Maintenance"><AdminMaintenanceRoute /></Shell>} />
      <Route path="/admin/emergency-controls" element={<Shell wide title="Admin · Emergency controls"><AdminEmergencyControlsRoute /></Shell>} />
      <Route path="/login" element={<Shell title="Sign in"><LoginPage /></Shell>} />
      <Route path="/signup" element={<Shell title="Create an account"><SignupPage /></Shell>} />
      <Route path="/verify-email" element={<Shell title="Verify your email"><VerifyEmailPage /></Shell>} />
      <Route path="/forgot-password" element={<Shell title="Reset your password"><ForgotPasswordPage /></Shell>} />
      <Route path="/reset-password" element={<Shell title="Choose a new password"><ResetPasswordPage /></Shell>} />
      <Route path="/account" element={<Shell title="My account"><AccountDashboard /></Shell>} />
      <Route path="/account/orders" element={<Shell title="My orders"><AccountOrdersPage /></Shell>} />
      <Route path="/account/claim-order" element={<Shell title="Claim an order"><ClaimOrderPage /></Shell>} />
      <Route path="/account/notifications" element={<Shell title="Delivery notifications"><AccountNotificationsPage /></Shell>} />
      <Route path="/account/preferences" element={<Shell title="Profile & preferences"><AccountPreferencesPage /></Shell>} />
      <Route path="/account/security" element={<Shell title="Sign-in history"><AccountSecurityPage /></Shell>} />
      <Route path="/account/privacy" element={<Shell title="Privacy & data"><AccountPrivacyPage /></Shell>} />
      <Route path="/account/reminders" element={<Shell title="Reminders"><AccountRemindersPage /></Shell>} />
      {/* WEB-03 §15.3 equivalent surfaces: the capability lives on one
          canonical page, so these named routes resolve to it. Saved
          recipients are the dashboard address book; export and deletion are
          actions on /account/privacy; sessions are the sign-in history on
          /account/security. */}
      <Route path="/account/orders/:id" element={<AccountOrderDetailRedirect />} />
      <Route path="/account/recipients" element={<Navigate to="/dashboard/address-book" replace />} />
      <Route path="/account/recipients/:id" element={<Navigate to="/dashboard/address-book" replace />} />
      <Route path="/account/privacy/export" element={<Navigate to="/account/privacy" replace />} />
      <Route path="/account/privacy/delete" element={<Navigate to="/account/privacy" replace />} />
      <Route path="/account/security/sessions" element={<Navigate to="/account/security" replace />} />
      <Route path="/account/deletion-confirm" element={<Shell title="Delete your account"><AccountDeletionConfirmPage /></Shell>} />
      <Route path="/claim-order" element={<ClaimOrderRedirect />} />
      <Route path="/how-it-works" element={<Shell bare title="How it works" description="Upload a photo, write a message, check the facility's mailing rules, preview both sides of the card, and send it for $3.99."><HowItWorksPage /></Shell>} />
      <Route path="/pricing" element={<Shell bare title="Pricing" description="$3.99 for one printed postcard, mailed. No account, no subscription, no add-ons."><PricingPage /></Shell>} />
      <Route path="/facility-rules" element={<Shell bare title="Facility mailing rules" description="The current BSO form and content rules for routine incoming postcards, with the source each one comes from."><FacilityRulesPage /></Shell>} />
      <Route path="/postcard-rules" element={<Shell bare title="Facility mailing rules" description="The current BSO form and content rules for routine incoming postcards, with the source each one comes from."><FacilityRulesPage /></Shell>} />
      <Route path="/accessibility" element={<Shell bare title="Accessibility" description="How CareCard Express is built to be usable with a keyboard, a screen reader, at zoom, and with reduced motion."><AccessibilityPage /></Shell>} />
      {/* WEB-02: the order id is in the path, the capability is in the
          fragment, and the fragment never reaches the server. */}
      <Route path="/order/:orderId" element={<Shell bare title="Your order"><PrivateOrderPage /></Shell>} />
      <Route path="/refund-policy" element={<Shell bare title="Refunds" description="What happens to your $3.99 when a card cannot be sent."><RefundPolicyPage /></Shell>} />
      <Route path="/cancellation-policy" element={<Shell bare title="Changing or cancelling an order" description="When a CareCard order can still be cancelled, and the point after which paper cannot be un-sent."><CancellationPolicyPage /></Shell>} />
      <Route path="/acceptable-use" element={<Shell bare title="Acceptable use" description="What CareCard Express may and may not be used for."><AcceptableUsePage /></Shell>} />
      <Route path="/content-policy" element={<Shell bare title="What can go on a card" description="The photo and message rules a card has to satisfy, and why a person reviews every one."><ContentPolicyPage /></Shell>} />
      <Route path="/data-retention" element={<Shell bare title="How long we keep things" description="What CareCard Express stores, how long it keeps it, and how to have it deleted sooner."><DataRetentionPage /></Shell>} />
      <Route path="/disclosures" element={<Shell bare title="Disclosures" description="Who runs CareCard Express, what it can and cannot confirm, and where it is not finished."><DisclosuresPage /></Shell>} />
      <Route path="/broward-photo-postcard-guide" element={<Shell bare title="How CareCard Sends a Physical Photo Postcard to Broward Detention Facilities" description="How CareCard Express creates, reviews, prints and mails a real $3.99 photo postcard to supported Broward detention facilities, with current facility-rule sources and no BSO affiliation.">
        <StructuredData data={{
          "@context": "https://schema.org", "@type": "HowTo",
          name: "How CareCard Express sends a physical photo postcard to Broward detention facilities",
          description: "Create and review a real physical photo postcard for $3.99, then CareCard handles printing and mailing after required checks pass.",
          totalCost: { "@type": "MonetaryAmount", currency: "USD", value: "3.99" },
          step: ["Upload your photo", "Write your message", "Review your postcard", "Pay $3.99", "CareCard prints and mails it"].map((name, i) => ({ "@type": "HowToStep", position: i + 1, name })),
        }} />
        <BrowardPostcardGuide />
      </Shell>} />
      <Route path="/facilities" element={<Shell bare title="Facilities we send to" description="The Broward Sheriff's Office detention facilities CareCard Express mails to, and which are currently available."><FacilitiesIndexPage /></Shell>} />
      <Route path="/facilities/:slug" element={<FacilityPage />} />
      <Route path="/policy-status" element={<Shell bare title="Where our rules come from" description="The official sources CareCard reads for each facility, when each was last checked, and where they disagree."><PolicyStatusPage /></Shell>} />
      <Route path="/help" element={<Shell bare title="Help" description="Track an order, read the facility mailing rules, change or cancel an order, or contact a person."><HelpPage /></Shell>} />
      <Route path="/help/order" element={<Shell bare title="Help with my order" description="Paid but no confirmation, a photo or message to fix, tracking that hasn't moved, or a rejected card — what can be done at each step."><OrderHelpPage /></Shell>} />
      {Object.entries(HELP_TOPIC_REDIRECTS).map(([topic, to]) => (
        <Route key={topic} path={`/help/${topic}`} element={<Navigate to={to} replace />} />
      ))}
      <Route path="/workbench" element={<Shell wide><Workbench /></Shell>} />
      {/* Port additions: user dashboard, admin ops boards, mail guide, services.
          These must stay above /:publicId — that catch-all would swallow them. */}
      <Route path="/dashboard" element={<Shell title="Dashboard" description="Your CareCard Express dashboard: past orders, referrals, photo credits, and scheduled sends."><Dashboard /></Shell>} />
      <Route path="/dashboard/orders" element={<Shell title="Past orders" description="Look up your past CareCard Express orders."><PastOrders /></Shell>} />
      <Route path="/dashboard/referrals" element={<Shell title="Referrals" description="Your Give $5, Get $5 referral dashboard."><ReferralDashboard /></Shell>} />
      <Route path="/dashboard/wallet" element={<Shell title="Photo credits" description="Your photo-credit wallet and ledger."><Wallet /></Shell>} />
      <Route path="/dashboard/scheduled" element={<Shell title="Scheduled sends" description="Manage your upcoming scheduled and recurring postcard sends."><ScheduledSends /></Shell>} />
      <Route path="/dashboard/notifications" element={<Shell title="Delivery notifications" description="Choose when CareCard Express notifies you about your cards."><NotifyPrefs /></Shell>} />
      <Route path="/dashboard/address-book" element={<Shell title="Address book" description="Your saved recipients for fast reordering."><AddressBook /></Shell>} />
      <Route path="/mail-guide" element={<Shell title="Mail guide" description="Facility mailing rules for Broward County detention facilities."><MailGuidePage /></Shell>} />
      <Route path="/services" element={<Shell title="Services" description="What CareCard Express offers and what it costs."><ServicesPage /></Shell>} />
      <Route path="/admin/pipeline" element={<Shell wide title="Pipeline board"><AdminPipelineView /></Shell>} />
      <Route path="/admin/rejections" element={<Shell wide title="Rejection tracker"><AdminRejectionsView /></Shell>} />
      <Route path="/admin/liability" element={<Shell wide title="Credit liability"><AdminLiabilityView /></Shell>} />
      <Route path="/admin/support" element={<Shell wide title="Support lookup"><AdminSupportView /></Shell>} />
      <Route path="/:publicId" element={<TrackingPage />} />
      {/* B-32: dedicated 404 page instead of a silent redirect to "/". */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </React.Suspense>
    </>
  );
}

// REACT_APP_HASH_ROUTER=1 builds a portable bundle whose routing works from
// any path — single-file demos, file://, sub-path static hosts.
const Router = process.env.REACT_APP_HASH_ROUTER ? HashRouter : BrowserRouter;

function SiteRouter() {
  return (
    <Router>
      <Site />
    </Router>
  );
}

export { SiteRouter };
export default SiteRouter;
